// Password-protected /admin page. Opened explicitly via #admin in
// the URL hash; the rest of the app never links to it. The page is
// stateless — every action posts to the API with the admin password
// in the `x-admin-password` header. No auth context, no token store,
// no React Router.
//
// Two columns: a "Mint codes" form on the left, a list of every
// existing code on the right (refreshed manually). Codes are shown
// verbatim so the admin can paste them into an email to a teacher.

import { useState } from "react";

import { ApiError, api, type AdminLicenseRow } from "../auth/client";

import { Logo } from "./Logo";

export function AdminPage() {
  const [password, setPassword] = useState("");
  const [authed, setAuthed] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);

  const [label, setLabel] = useState("");
  const [count, setCount] = useState(1);
  const [minted, setMinted] = useState<string[]>([]);
  const [pending, setPending] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const [licenses, setLicenses] = useState<AdminLicenseRow[]>([]);
  const [listing, setListing] = useState(false);

  const tryAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError(null);
    if (password.length < 8) {
      setAuthError("Admin password must be at least 8 characters.");
      return;
    }
    // Probe by listing — successful list = correct password.
    try {
      const { licenses } = await api.adminListLicenses(password);
      setLicenses(licenses);
      setAuthed(true);
    } catch (err) {
      if (err instanceof ApiError && err.code === "unauthorized") {
        setAuthError("Wrong password.");
      } else if (err instanceof ApiError && err.code === "admin_disabled") {
        setAuthError(
          "Admin endpoints are disabled on this deploy (ADMIN_PASSWORD missing on the API).",
        );
      } else {
        setAuthError(err instanceof Error ? err.message : "Unknown error.");
      }
    }
  };

  const mint = async (e: React.FormEvent) => {
    e.preventDefault();
    setActionError(null);
    setPending(true);
    try {
      const { codes } = await api.adminMintLicenses(
        password,
        label.trim() || undefined,
        count,
      );
      setMinted(codes);
      // Refresh the list so the new codes show up in the audit
      // pane without the admin having to click "Refresh".
      const { licenses } = await api.adminListLicenses(password);
      setLicenses(licenses);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Mint failed.");
    } finally {
      setPending(false);
    }
  };

  const refresh = async () => {
    setListing(true);
    try {
      const { licenses } = await api.adminListLicenses(password);
      setLicenses(licenses);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Refresh failed.");
    } finally {
      setListing(false);
    }
  };

  if (!authed) {
    return (
      <div className="license-gate">
        <div className="license-card admin-card">
          <div className="license-header">
            <Logo size={56} />
            <div>
              <h1>Admin · CoScience.Co</h1>
              <p className="license-tag">License-code management</p>
            </div>
          </div>
          <form className="license-form" onSubmit={tryAuth}>
            <label className="license-field">
              <span>Admin password</span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoFocus
              />
            </label>
            {authError && <div className="license-error">{authError}</div>}
            <button
              type="submit"
              className="license-submit"
              disabled={password.length === 0}
            >
              Sign in
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <header className="admin-header">
        <Logo size={36} />
        <h1>Admin · CoScience.Co</h1>
        <a href="/" className="admin-back">
          ← Back to sandbox
        </a>
      </header>

      <div className="admin-grid">
        <section className="admin-section">
          <h2>Mint codes</h2>
          <form className="admin-form" onSubmit={mint}>
            <label>
              <span>Label (audit only)</span>
              <input
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g. Greenfield High School"
              />
            </label>
            <label>
              <span>How many codes?</span>
              <input
                type="number"
                min={1}
                max={50}
                value={count}
                onChange={(e) => setCount(Number(e.target.value))}
              />
            </label>
            <button type="submit" disabled={pending}>
              {pending ? "..." : "Mint"}
            </button>
          </form>
          {actionError && <div className="license-error">{actionError}</div>}
          {minted.length > 0 && (
            <div className="admin-minted">
              <h3>Newly minted (copy these, they won't be highlighted again):</h3>
              <ul>
                {minted.map((code) => (
                  <li key={code}>
                    <code>{code}</code>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>

        <section className="admin-section">
          <div className="admin-section-header">
            <h2>All codes ({licenses.length})</h2>
            <button onClick={refresh} disabled={listing}>
              {listing ? "..." : "Refresh"}
            </button>
          </div>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Status</th>
                <th>Label</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {licenses.map((row) => (
                <tr key={row.code} className={row.redeemed ? "redeemed" : ""}>
                  <td>
                    <code>{row.code}</code>
                  </td>
                  <td>
                    {row.redeemed
                      ? `Used ${row.redeemed_at?.slice(0, 10) ?? ""}`
                      : "Unused"}
                  </td>
                  <td>{row.created_by}</td>
                  <td>{row.created_at.slice(0, 10)}</td>
                </tr>
              ))}
              {licenses.length === 0 && (
                <tr>
                  <td colSpan={4} className="admin-empty">
                    No codes minted yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </section>
      </div>
    </div>
  );
}
